import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FormProgress } from '../types/form';
import { contractTemplate } from './contractTemplate';
import { numberToWords } from './numberToWords';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatMoney = (value: number | undefined): string =>
  (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

const formatContent = (content: string): string =>
  escapeHtml(content.trim()).replace(/\n/g, '<br />');

export function generateContractWord(formData: FormProgress['data']): Blob {
  const customer = formData.customerInfo;
  const location = formData.installationLocation;
  const technical = formData.technicalConfig;
  const financial = formData.financialTerms;
  const installments = financial?.installments || [];
  const systemPower = (
    ((technical?.solarModules.power || 0) * (technical?.solarModules.quantity || 0)) /
    1000
  ).toFixed(2);
  const requiredArea = (technical?.solarModules.quantity || 0) * 3;
  const today = format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const city = location?.city || '';

  const paymentTerms =
    installments.length === 1
      ? `Pagamento único no valor de R$ ${formatMoney(installments[0].amount)} a ser pago via ${escapeHtml(installments[0].method)} com vencimento em ${format(installments[0].dueDate, 'dd/MM/yyyy')}.`
      : installments
          .map(
            (installment, index) =>
              `Parcela ${index + 1}: R$ ${formatMoney(installment.amount)} via ${escapeHtml(installment.method)} com vencimento em ${format(installment.dueDate, 'dd/MM/yyyy')}`
          )
          .join('\n');

  const clientDuties = contractTemplate.clauses.clientDuties.replace(
    '4.6 Disponibilizar área mínima de 60m² necessária para instalação do projeto;',
    `4.6 Disponibilizar área mínima de ${requiredArea}m² necessária para instalação do projeto;`
  );
  const inverter1Warranty = `${technical?.inverter1.warrantyPeriod || 0} (${numberToWords(technical?.inverter1.warrantyPeriod || 0)}) anos contra defeitos de fabricação e perda de eficiência energética`;
  const inverter2Warranty = technical?.inverter2
    ? `\n   - ${technical.inverter2.warrantyPeriod} (${numberToWords(technical.inverter2.warrantyPeriod || 0)}) anos contra defeitos de fabricação e perda de eficiência energética`
    : '';
  const warranty = contractTemplate.clauses.warranties.replace(
    'b) Inversores:\n   - 10 (dez) anos contra defeitos de fabricação e perda de eficiência energética',
    `b) Inversores:\n   - ${inverter1Warranty}${inverter2Warranty}`
  );
  const deadlines = contractTemplate.clauses.deadlines.replace(
    '5.1 O cronograma será avaliado conforme liberação das atividades. Prevê-se 50 dias corridos após pagamento dos materiais para conclusão da obra.',
    `5.1 O cronograma será avaliado conforme liberação das atividades. Prevê-se ${technical?.installationDays || 0} (${numberToWords(technical?.installationDays || 0)}) dias corridos após pagamento dos materiais para conclusão da obra.`
  );

  const objectContent = `1.1 A CONTRATADA compromete-se a prestar os serviços de instalação do sistema fotovoltaico, bem como tratativas com a concessionária de distribuição de energia elétrica. O sistema presente tem como potência ${systemPower}kWp (Quilo Watts Pico).

Dentre os principais materiais estão inclusos:
- ${technical?.inverter1.quantity || 0} Inversor(es) ${technical?.inverter1.brand || ''} de ${technical?.inverter1.power || 0}kW de Potência de Saída${technical?.inverter2 ? `\n- ${technical.inverter2.quantity} Inversor(es) ${technical.inverter2.brand} de ${technical.inverter2.power}kW de Potência de Saída` : ''}
- ${technical?.solarModules.quantity || 0} Módulos Solares ${technical?.solarModules.brand || ''} ${technical?.solarModules.power || 0}W de Potência
- Estrutura para fixação dos módulos instalados em ${technical?.installationType === 'Roof' ? 'Telhado' : 'Solo'}

Parágrafo 1º: A CONTRATADA poderá, caso necessário, substituir marcas, modelos ou potências dos equipamentos especificados (como módulos, inversores e demais componentes), desde que os itens substituídos possuam qualidade técnica equivalente ou superior. Tal substituição poderá ocorrer por motivos como indisponibilidade em estoque, descontinuidade do fornecedor ou necessidade de manutenção do cronograma da obra.`;

  const priceContent = `2.1 O CONTRATANTE pagará à CONTRATADA, pelos serviços descritos na cláusula primeira deste compromisso, a quantia de R$ ${formatMoney(financial?.totalAmount)}, referentes aos materiais, mão de obra, engenharia, serviços de projetos e aprovações.

2.2 Fica acordado o pagamento nas seguintes condições:
${paymentTerms}

2.3 O não cumprimento pelo CONTRATANTE das datas e prazos avençados nesta cláusula o constituirá em mora, independentemente de interpelação da CONTRATADA, nos termos do artigo 397 do Código Civil. Ainda, acarretará à CONTRATANTE multa equivalente a 2% do valor total, acrescidos de juros de mora de 3% ao mês, contados a partir do inadimplemento da obrigação.

2.4 Quaisquer taxas, custas, ou acréscimos que surjam em decorrência de alteração da avença acima, ou de prazos de boletos, ou mesmo de renegociações, serão de responsabilidade do CONTRATANTE.`;

  const sections: Array<[string, string]> = [
    ['1 - DO OBJETO DO PRESENTE CONTRATO', objectContent],
    ['2 - PREÇO E FORMA DE PAGAMENTO', priceContent],
    ['3 - DOS DEVERES E OBRIGAÇÕES DA CONTRATADA', contractTemplate.clauses.contractorDuties],
    ['4 - DOS DEVERES E OBRIGAÇÕES DO CONTRATANTE', clientDuties],
    ['5 - PRAZOS E CRONOGRAMA', deadlines],
    ['6 - DA RESCISÃO E DAS PENALIDADES CONTRATUAIS', contractTemplate.clauses.termination],
    ['7 - DAS GARANTIAS', warranty],
    ['8 - DA LIMITAÇÃO DE RESPONSABILIDADE SOBRE TARIFAS E REGULAMENTOS', contractTemplate.clauses.liabilityOnTariffs],
    ['9 - DA ALTERAÇÃO DE PERFIL DE CONSUMO E DIMENSIONAMENTO DO SISTEMA', contractTemplate.clauses.consumptionProfile],
    ['10 - DISPOSIÇÕES FINAIS', contractTemplate.clauses.finalProvisions]
  ];

  const sectionHtml = sections
    .map(([title, content]) => {
      const cleanedContent = content.replace(/^\d+\s*-.*\n?/, '');
      return `<h2>${escapeHtml(title)}</h2><p>${formatContent(cleanedContent)}</p>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" lang="pt-BR">
<head><meta charset="utf-8"><title>Contrato de Prestação de Serviços</title>
<style>
@page { size: 21cm 29.7cm; margin: 2cm; }
body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #000; }
h1 { text-align: center; font-size: 16pt; margin: 0 0 24pt; }
h2 { font-size: 12pt; margin: 18pt 0 6pt; page-break-after: avoid; }
p { text-align: justify; margin: 0 0 10pt; }
.header { text-align: right; margin-bottom: 18pt; }
.header img { width: 120px; height: auto; }
.signatures { width: 100%; margin-top: 34pt; border-collapse: collapse; }
.signatures td { width: 50%; text-align: center; vertical-align: top; padding: 0 20pt; }
.line { border-top: 1px solid #000; margin-bottom: 8pt; }
.witnesses { margin-top: 30pt; }
</style></head>
<body>
  <div class="header"><img src="https://i.imgur.com/J9lB0MV.jpg" alt="ECOENERGI SOLAR"></div>
  <h1>CONTRATO DE PRESTAÇÃO DE SERVIÇOS</h1>
  <h2>IDENTIFICAÇÃO DAS PARTES CONTRATADAS</h2>
  <p><strong>CONTRATADA:</strong> ECOENERGI SOLAR, PESSOA JURÍDICA DE DIREITO PRIVADO, INSCRITA NO CNPJ SOB O N° 12.276.329.0001-69, COM SEDE NA RUA DEPUTADO JÚLIO CÉSAR PAULINO MAIA - N°1346- CENTRO, NA CIDADE DE SANTA RITA DO PARDO - MS, COM CEP: 79690-000, NESTE ATO REPRESENTADA POR DIOGO CASTRO ALVES RODRIGUES, INSCRITO NO CPF SOB O N° 038.281.431-21.</p>
  <p><strong>CONTRATANTE:</strong> ${escapeHtml((customer?.fullName || '').toUpperCase())}, PORTADOR(A) DO CPF/CNPJ ${escapeHtml(customer?.cpf || '')} E RG ${escapeHtml(customer?.rg || '')} ${escapeHtml((customer?.issuingAuthority || '').toUpperCase())}, ${escapeHtml((customer?.nationality || '').toUpperCase())}, ${escapeHtml((customer?.profession || '').toUpperCase())}, RESIDENTE E DOMICILIADO(A) EM ${escapeHtml((location?.street || '').toUpperCase())}, ${escapeHtml(location?.number || '')}, ${escapeHtml((location?.neighborhood || '').toUpperCase())}, ${escapeHtml((location?.city || '').toUpperCase())}/${escapeHtml(location?.state || '')}, CEP ${escapeHtml(location?.zipCode || '')}, TELEFONE ${escapeHtml(customer?.phone || '')}, EMAIL ${escapeHtml(customer?.email || '')}.</p>
  ${sectionHtml}
  <p style="text-align:right; margin-top:24pt;">${escapeHtml(city)}, ${today}</p>
  <table class="signatures"><tr><td><div class="line"></div>ECOENERGI SOLAR<br>CNPJ: 12.276.329.0001-69</td><td><div class="line"></div>${escapeHtml(customer?.fullName || '')}<br>CPF: ${escapeHtml(customer?.cpf || '')}</td></tr></table>
  <div class="witnesses"><strong>TESTEMUNHAS:</strong><table class="signatures"><tr><td><div class="line"></div>Nome:<br>CPF:</td><td><div class="line"></div>Nome:<br>CPF:</td></tr></table></div>
</body></html>`;

  return new Blob([html], { type: 'application/msword;charset=utf-8' });
}
